import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../types/ServiceStoreTypes";
import { findOrThrow, isNotFoundError, loadErrorMessage } from "../../shared/loadOrError";
import { findAndEvict } from "../../shared/findAndEvict";
import { THROW_ON_ERROR } from "../../shared/dialogSubmit";
import {
  addWeeks,
  currentWeekStart,
  laneStatusMeta,
  weekStartOf,
  type LaneStatusCategory,
  type LaneStatusColor,
  type LaneStatusMeta,
  type LaneWork,
  type LaneWorkResponse,
  type WhiteboardBoard,
  type WhiteboardCellRecord,
  type WhiteboardItemKind,
  type WhiteboardItemRecord,
  type WhiteboardItemStatus,
  type WhiteboardRowRecord,
  type WhiteboardStatusRecord,
  type WeekStatusFilter,
  type WhiteboardStatus,
} from "./whiteboard.types";
import {
  defaultDueFor,
  isOpenItem,
  myWeek,
  overdueItems,
  slippingLanes,
  todayLocal,
  type MyWeek,
  type SlippingLane,
} from "./whiteboardTracking";
import { currentUserIdOf } from "../../auth/userId";
import { isAdmin, isOperator } from "../../auth/tier";
import { subscribeMeetingIntelEvents } from "../meetingIntelligence/meetingIntelligenceRealtime";

import { sessionMark } from "stores/sessionGuard";
/** Weeks shown on the board: last week, this week, and the next two. */
export const WEEKS_BEFORE = 1;
export const WEEKS_AFTER = 2;

/** The board's reads: every lane, and every cell of the loaded week window. */
const ROWS_LIMIT = 500;
const CELLS_LIMIT = 2000;
/** Entries: every OPEN one (whatever its week — Overdue must never lose one)
 *  plus every one in the loaded window. */
const ITEMS_LIMIT = 3000;
const ITEMS_SLACK_WEEKS = 1;
const STATUSES_LIMIT = 200;
const USERS_LIMIT = 500;
/** An accepted/completed action item re-reads the lanes' work this long after. */
const LANE_WORK_DEBOUNCE_MS = 1500;

/** The page's three views (#3383 My week; #3078 + Blake: Overdue & slipping). */
export type WhiteboardView = "board" | "my-week" | "slipping";
export const WHITEBOARD_VIEWS: ReadonlyArray<{ value: WhiteboardView; label: string }> = [
  { value: "board", label: "Board" },
  { value: "my-week", label: "My week" },
  { value: "slipping", label: "Overdue & slipping" },
];
const isView = (value: unknown): value is WhiteboardView =>
  WHITEBOARD_VIEWS.some((v) => v.value === value);

/** The entry dialog (route dialog on the Whiteboard). */
export const WHITEBOARD_ENTRY_DIALOG = "WhiteboardEntryDialog";
/** The deal-status editor (admins, route dialog on the Whiteboard). */
export const WHITEBOARD_STATUSES_DIALOG = "WhiteboardStatusesDialog";
export const LOG_ENTRY_LABEL = "Log entry";
export const LOG_ENTRY_ARIA_LABEL = "Log a weekly goal, milestone or blocker";

/** The Filters button's "Deal status" choice on the Sales Queue: a category, or one status. */
export type DealStatusFilter = `category:${LaneStatusCategory}` | `status:${string}`;

/** State behind the cell-edit dialog (created empty for an untouched week). */
export interface CellDraft {
  rowId: string;
  rowTitle: string;
  weekOf: string;
  /** Existing cell id, or null when this week has no cell yet. */
  cellId: string | null;
  status: WhiteboardStatus | string;
  note: string;
}

/** State behind the add/edit-lane dialog. */
export interface RowDraft {
  /** Existing row id, or null when adding. */
  rowId: string | null;
  board: WhiteboardBoard;
  clientId: string | null;
  title: string;
  resource: string;
  workstream: string;
  notes: string;
  status: WhiteboardStatus | string | null;
  /** Target delivery / go-live date, 'YYYY-MM-DD' or "" for none. */
  targetDate: string;
  /** What the date is ("Go-live", "Delivery"…), or "". */
  targetLabel: string;
  /**
   * Set by Move to Delivery (#3558): the draft is a Sales Queue lane on its
   * way to the Delivery board. It must name a client, and saving it makes
   * that client Active if it is still a Prospect.
   */
  moveToDelivery?: boolean;
}

/** State behind the entry dialog (#3383). */
export interface ItemDraft {
  /** Existing entry id, or null when logging a new one. */
  itemId: string | null;
  rowId: string | null;
  kind: WhiteboardItemKind;
  title: string;
  note: string;
  /** Whose it is. Null = the signed-in person (the server's default). */
  ownerUserId: number | null;
  /** The week's Monday. */
  weekOf: string;
  /** 'YYYY-MM-DD', or "" for none (then the end of its week counts). */
  dueDate: string;
  status: WhiteboardItemStatus;
}

/** The My week quick-add row: one line per entry, the signed-in person as owner. */
export interface QuickDraft {
  rowId: string | null;
  kind: WhiteboardItemKind;
  title: string;
  weekOf: string;
  dueDate: string;
}

const emptyQuickDraft = (): QuickDraft => {
  const week = currentWeekStart();
  return { rowId: null, kind: "goal", title: "", weekOf: week, dueDate: defaultDueFor(week) };
};

/** One deal status being edited in the statuses dialog. */
export interface StatusEdit {
  label: string;
  color: LaneStatusColor | string;
  category: LaneStatusCategory | string;
}

const emptyStatusEdit = (): StatusEdit => ({ label: "", color: "grey", category: "active" });

/** A staff member as the board names them. */
export interface WhiteboardPerson {
  id: number;
  name: string;
}

/**
 * The Add lane control, shared by the Whiteboard header and Client 360's
 * Whiteboard tab so the two never drift apart. The dialog is registered on
 * BOTH routes (whiteboard.Route.ts, clientDetail.Route.ts).
 */
export const WHITEBOARD_ROW_DIALOG = "WhiteboardRowDialog";
export const ADD_LANE_LABEL = "Add lane";
export const ADD_LANE_ARIA_LABEL = "Add a whiteboard lane";

/** The Sales Queue lane's action that moves it onto the Delivery board. */
export const MOVE_TO_DELIVERY_LABEL = "Move to Delivery";

/**
 * Why Archive lane and Clear cell are not live for an operator (2026-11-02:
 * both became an administrator's). Shown beside the disabled control; the API
 * refuses either with its own sentence, which lands on `removeError` should a
 * call ever be made.
 */
export const LANE_ARCHIVE_ADMIN_ONLY =
  "Only an administrator can archive a lane: archiving takes the lane and its weekly notes off the board for everyone.";
export const WEEK_CLEAR_ADMIN_ONLY =
  "Only an administrator can clear a week: clearing removes the week's status and note for everyone. You can still change them.";

/** A client's lifecycle status as `clients.status` holds it. */
const isProspectStatus = (status: unknown): boolean =>
  typeof status === "string" && status.trim().toLowerCase() === "prospect";

/** What a move did to its client: made it Active, found it was not a
 *  Prospect, or found it had stopped being one since the board loaded. */
type Activation = "activated" | "not-prospect" | "changed";

/** Minimal client shape for the lane dialog's client picker. */
export interface WhiteboardClientOption {
  id: string;
  name: string;
}

/**
 * View store for the Whiteboard — the team's delivery + sales whiteboard as a
 * native route. Rule: route ViewStores are plain classes with
 * `makeAutoObservable` and do NOT extend ViewStore. Rows and cells live in the
 * registered `whiteboard-rows` / `whiteboard-cells` ServiceStore caches; every
 * list here is a computed view over them. Client 360 ties in both directions:
 * a lane's client opens ClientDetail, and ClientDetail's Whiteboard tab calls
 * `focusClient` before navigating here.
 */
class WhiteboardStore {
  rootStore: RootStore;

  loading = false;
  /** Lanes failed to load — the board must not read as "no lanes yet". */
  error: string | null = null;
  /** The week window's cells failed to load — every lane would otherwise read
   *  as "Nothing scheduled". Kept apart from `error` so paging weeks can clear
   *  its own failure without hiding a lanes failure. */
  cellsError: string | null = null;

  board: WhiteboardBoard = "delivery";
  searchQuery = "";
  /** Non-null ⇒ the board is narrowed to one client (set from Client 360, or
   *  the header's Filters button). */
  clientFilter: string | null = null;
  /** Non-null ⇒ only lanes this person owns (the lane's Resource). */
  resourceFilter: string | null = null;
  /** Non-null ⇒ only lanes whose cell for this week has this status. */
  weekStatusFilter: WeekStatusFilter | null = null;

  /** The Monday anchoring the visible week window. */
  weekAnchor: string = currentWeekStart();

  processing = false;
  /**
   * Why the open dialog's save was refused. The dialog routes throw it, so it
   * shows once, in the dialog's footer (shared/dialogSubmit).
   */
  actionError: string | null = null;
  /**
   * Why the open dialog's Clear cell / Archive lane was refused. Those run from
   * a button inside the dialog body, not its submit, so the body shows this.
   */
  removeError: string | null = null;

  cellDraft: CellDraft | null = null;
  rowDraft: RowDraft | null = null;

  /** Which view the page shows; `?view=` deep-links one (whiteboard.Route.ts). */
  view: WhiteboardView = "board";
  /** Slipping's "within N days of its date". */
  slippingHorizonDays = 14;
  /** Non-null ⇒ the Sales Queue shows only this deal status (or category). */
  dealStatusFilter: DealStatusFilter | null = null;
  /** The day Overdue is measured against; refreshed on every load. */
  today: string = todayLocal();

  /** Entries failed to load: Overdue and My week must say so, not "nothing". */
  itemsError: string | null = null;
  /** The deal statuses failed to load (lane chips fall back to their keys). */
  statusesError: string | null = null;
  /** The lanes' accepted work, as `whiteboard-lane-work` last answered. */
  laneWork: LaneWork[] = [];
  laneWorkMeta: Omit<LaneWorkResponse, "data" | "total"> | null = null;
  laneWorkError: string | null = null;

  itemDraft: ItemDraft | null = null;
  quickDraft: QuickDraft = emptyQuickDraft();
  /** Why the quick add was refused (shown under it). */
  quickError: string | null = null;
  /** Why a Done / Drop / new date from a list was refused (shown above the list). */
  listError: string | null = null;

  /** The statuses dialog: edits not yet saved, by status id, and the new-status row. */
  statusEdits: Record<string, StatusEdit> = {};
  newStatus: StatusEdit = emptyStatusEdit();
  /** Why the editor's Save was refused: thrown into the dialog's footer. */
  statusError: string | null = null;
  /** Why an add, retire, restore or reorder (buttons inside the editor) was refused: shown in its body. */
  statusWriteError: string | null = null;

  private laneWorkUnsubscribe: (() => void) | null = null;
  private laneWorkTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    // The subscription handle and the debounce timer are plumbing, not state:
    // nothing renders them, and the timer is set from a socket callback.
    makeAutoObservable<this, "laneWorkUnsubscribe" | "laneWorkTimer">(this, {
      rootStore: false,
      laneWorkUnsubscribe: false,
      laneWorkTimer: false,
    });
  }

  private get rowsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("whiteboard-rows") ??
        this.rootStore.whiteboardRowsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get cellsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("whiteboard-cells") ??
        this.rootStore.whiteboardCellsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get clientsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("clients") ??
        this.rootStore.clientStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get itemsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("whiteboard-items") ??
        this.rootStore.whiteboardItemsStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get statusesService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("whiteboard-statuses") ??
        this.rootStore.whiteboardStatusesStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get laneWorkService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("whiteboard-lane-work") ??
        this.rootStore.whiteboardLaneWorkStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  private get usersService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("user") ??
        this.rootStore.userStore) as unknown as ServiceStoreType | null) ?? null
    );
  }

  // ---- Derived data -------------------------------------------------------

  /** Any failure that makes the board's rows/cells untrustworthy. */
  get loadError(): string | null {
    return this.error ?? this.cellsError;
  }

  /** True when a search or any filter narrows the lanes. */
  get hasActiveFilter(): boolean {
    return this.activeFilterCount > 0 || this.searchQuery.trim().length > 0;
  }

  /** How many of the Filters button's filters are set: its badge. */
  get activeFilterCount(): number {
    return (
      (this.clientFilter ? 1 : 0) +
      (this.resourceFilter ? 1 : 0) +
      (this.weekStatusFilter ? 1 : 0) +
      (this.board === "sales" && this.dealStatusFilter ? 1 : 0)
    );
  }

  /** Live lanes of the active board, before any filter. */
  private get boardRows(): WhiteboardRowRecord[] {
    return this.allRows.filter((r) => !r.deletedAt && r.board === this.board);
  }

  /**
   * The Client filter's choices: the clients with a lane on this board, named
   * from the clients list (or the lane's title while that list is not loaded),
   * plus the client the board is narrowed to, so the field can always show it.
   */
  get clientFilterOptions(): WhiteboardClientOption[] {
    const byId = new Map<string, WhiteboardClientOption>();
    for (const row of this.boardRows) {
      if (!row.clientId || byId.has(row.clientId)) continue;
      byId.set(row.clientId, {
        id: row.clientId,
        name: this.clientName(row.clientId) ?? row.title,
      });
    }
    if (this.clientFilter && !byId.has(this.clientFilter)) {
      byId.set(this.clientFilter, {
        id: this.clientFilter,
        name: this.clientName(this.clientFilter) ?? this.clientFilter,
      });
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  /** The Resource filter's choices: every owner named on this board's lanes. */
  get resourceOptions(): string[] {
    const byKey = new Map<string, string>();
    for (const row of this.boardRows) {
      const resource = row.resource?.trim();
      if (resource && !byKey.has(resource.toLowerCase())) {
        byKey.set(resource.toLowerCase(), resource);
      }
    }
    return [...byKey.values()].sort((a, b) => a.localeCompare(b));
  }

  get allRows(): WhiteboardRowRecord[] {
    return (this.rowsService?.entities ?? []) as unknown as WhiteboardRowRecord[];
  }

  get allCells(): WhiteboardCellRecord[] {
    return (this.cellsService?.entities ?? []) as unknown as WhiteboardCellRecord[];
  }

  /** Client options for the lane dialog and name resolution on the board. */
  get clients(): WhiteboardClientOption[] {
    const list = (this.clientsService?.entities ?? []) as unknown as Array<{
      id: string;
      name?: string;
    }>;
    return list
      .map((c) => ({ id: c.id, name: c.name || c.id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  clientName(clientId?: string | null): string | null {
    if (!clientId) return null;
    return this.clients.find((c) => c.id === clientId)?.name ?? null;
  }

  /** True when the clients list holds this client as a Prospect; false when it
   *  holds another status or does not hold the client. */
  clientIsProspect(clientId?: string | null): boolean {
    if (!clientId) return false;
    const entities = (this.clientsService?.entities ?? []) as unknown as Array<{
      id: string;
      status?: unknown;
    }>;
    return isProspectStatus(entities.find((c) => c.id === clientId)?.status);
  }

  /** The visible week columns (Mondays), anchor-relative. */
  get weeks(): string[] {
    const start = addWeeks(this.weekAnchor, -WEEKS_BEFORE);
    return Array.from({ length: WEEKS_BEFORE + 1 + WEEKS_AFTER }, (_v, i) =>
      addWeeks(start, i)
    );
  }

  get currentWeek(): string {
    return currentWeekStart();
  }

  /**
   * Lanes of the active board, filtered and ordered: client lanes first
   * (grouped by title so an engagement's resource lanes sit together), then
   * internal lanes; sortOrder wins within a group.
   */
  get filteredRows(): WhiteboardRowRecord[] {
    let rows = this.boardRows;
    if (this.clientFilter) {
      rows = rows.filter((r) => r.clientId === this.clientFilter);
    }
    if (this.resourceFilter) {
      const owner = this.resourceFilter.toLowerCase();
      rows = rows.filter((r) => r.resource?.trim().toLowerCase() === owner);
    }
    if (this.board === "sales" && this.dealStatusFilter) {
      const wanted = this.dealStatusFilter;
      rows = rows.filter((r) =>
        wanted.startsWith("category:")
          ? this.laneStatusMeta(r.status)?.category === wanted.slice("category:".length)
          : r.status === wanted.slice("status:".length),
      );
    }
    if (this.weekStatusFilter) {
      const wanted = this.weekStatusFilter;
      const week = this.currentWeek;
      rows = rows.filter((r) => {
        const status = this.cellFor(r.id, week)?.status ?? null;
        return wanted === "none" ? status === null : status === wanted;
      });
    }
    const q = this.searchQuery.toLowerCase().trim();
    if (q) {
      rows = rows.filter(
        (r) =>
          r.title?.toLowerCase().includes(q) ||
          r.resource?.toLowerCase().includes(q) ||
          r.workstream?.toLowerCase().includes(q) ||
          r.notes?.toLowerCase().includes(q)
      );
    }
    return rows.slice().sort((a, b) => {
      const aClient = a.clientId ? 0 : 1;
      const bClient = b.clientId ? 0 : 1;
      if (aClient !== bClient) return aClient - bClient;
      const byTitle = (a.title || "").localeCompare(b.title || "");
      if (byTitle !== 0) return byTitle;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
  }

  /**
   * Live cells keyed `rowId|week`. The board asks for one cell per lane-week it
   * draws (40 lanes x 4 weeks), and each ask used to scan every cached cell —
   * re-parsing its week key — so drawing the board cost lanes x weeks x cells.
   * One pass now, recomputed only when the cells change. The first live cell
   * of a lane-week wins, as the scan's `find` did.
   */
  get cellIndex(): Map<string, WhiteboardCellRecord> {
    const index = new Map<string, WhiteboardCellRecord>();
    for (const cell of this.allCells) {
      if (cell.deletedAt) continue;
      const key = `${cell.rowId}|${weekStartOf(cell.weekOf)}`;
      if (!index.has(key)) index.set(key, cell);
    }
    return index;
  }

  /** Live cell for one lane-week, if any. */
  cellFor(rowId: string, weekOf: string): WhiteboardCellRecord | null {
    return this.cellIndex.get(`${rowId}|${weekOf}`) ?? null;
  }

  // ---- Weekly entries (#3383), Overdue (#3078), Slipping (Blake) ---------

  get allItems(): WhiteboardItemRecord[] {
    return (this.itemsService?.entities ?? []) as unknown as WhiteboardItemRecord[];
  }

  /** Entries not archived. */
  get liveItems(): WhiteboardItemRecord[] {
    return this.allItems.filter((item) => !item.deletedAt);
  }

  /** Live entries keyed `rowId|week`: goals, then milestones, then blockers, each by due day. */
  get itemIndex(): Map<string, WhiteboardItemRecord[]> {
    const order: Record<string, number> = { goal: 0, milestone: 1, blocker: 2 };
    const index = new Map<string, WhiteboardItemRecord[]>();
    for (const item of this.liveItems) {
      const key = `${item.rowId}|${weekStartOf(item.weekOf)}`;
      const list = index.get(key);
      if (list) list.push(item);
      else index.set(key, [item]);
    }
    for (const list of index.values()) {
      list.sort(
        (a, b) =>
          (order[a.kind] ?? 3) - (order[b.kind] ?? 3) ||
          String(a.dueDate ?? "").localeCompare(String(b.dueDate ?? "")) ||
          a.title.localeCompare(b.title),
      );
    }
    return index;
  }

  /** One lane-week's entries. */
  itemsFor(rowId: string, weekOf: string): WhiteboardItemRecord[] {
    return this.itemIndex.get(`${rowId}|${weekOf}`) ?? [];
  }

  /** Open blockers by lane, whatever their week, each lane's oldest first. One pass. */
  get openBlockersByLane(): Map<string, WhiteboardItemRecord[]> {
    const byLane = new Map<string, WhiteboardItemRecord[]>();
    for (const item of this.liveItems) {
      if (item.kind !== "blocker" || !isOpenItem(item)) continue;
      const list = byLane.get(item.rowId);
      if (list) list.push(item);
      else byLane.set(item.rowId, [item]);
    }
    for (const list of byLane.values()) {
      list.sort((a, b) => String(a.createdAt ?? "").localeCompare(String(b.createdAt ?? "")));
    }
    return byLane;
  }

  /** Every open blocker on a lane, whatever its week, the oldest first. */
  openBlockersFor(rowId: string): WhiteboardItemRecord[] {
    return this.openBlockersByLane.get(rowId) ?? [];
  }

  /** Every overdue entry on either board, the longest overdue first (#3078). */
  get overdue(): WhiteboardItemRecord[] {
    return overdueItems(this.liveItems, this.today);
  }

  /** Lanes by id, archived included (an entry names its lane even after it goes). */
  get laneById(): Map<string, WhiteboardRowRecord> {
    return new Map(this.allRows.map((row) => [row.id, row]));
  }

  laneFor(rowId: string | null | undefined): WhiteboardRowRecord | null {
    return rowId ? (this.laneById.get(rowId) ?? null) : null;
  }

  /** "H2 Precast · Erick": how an entry names its lane. */
  laneLabel(rowId: string | null | undefined): string {
    const lane = this.laneFor(rowId);
    if (!lane) return "A lane no longer on the board";
    return [lane.title, lane.resource].filter(Boolean).join(" · ");
  }

  /**
   * The lanes an entry may be logged on: every live lane of both boards, the
   * board you are on first, and on each board client lanes before internal
   * ones, as the board orders them.
   */
  get laneOptions(): Array<{ id: string; label: string; board: WhiteboardBoard }> {
    return this.allRows
      .filter((row) => !row.deletedAt)
      .slice()
      .sort((a, b) => {
        const aBoard = a.board === this.board ? 0 : 1;
        const bBoard = b.board === this.board ? 0 : 1;
        if (aBoard !== bBoard) return aBoard - bBoard;
        const aClient = a.clientId ? 0 : 1;
        const bClient = b.clientId ? 0 : 1;
        if (aClient !== bClient) return aClient - bClient;
        return (a.title || "").localeCompare(b.title || "") || (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      })
      .map((row) => ({
        id: row.id,
        label: [row.title, row.resource].filter(Boolean).join(" · "),
        board: row.board,
      }));
  }

  /** Staff members by id, from the user list (names entries' owners). */
  get people(): Map<number, WhiteboardPerson> {
    const list = (this.usersService?.entities ?? []) as unknown as Array<{
      id: number | string;
      firstName?: string | null;
      lastName?: string | null;
      email?: string | null;
      isValidated?: boolean | number | null;
    }>;
    const byId = new Map<number, WhiteboardPerson>();
    for (const user of list) {
      const id = Number(user.id);
      if (!Number.isSafeInteger(id)) continue;
      const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.email || `User ${id}`;
      byId.set(id, { id, name });
    }
    return byId;
  }

  /** Who an entry's owner is, said honestly when HQ cannot name them. */
  personName(userId: number | null | undefined): string {
    if (userId === null || userId === undefined) return "Nobody";
    return this.people.get(Number(userId))?.name ?? `User #${userId}`;
  }

  /** The owner picker's choices (operators and admins may log for anyone). */
  get peopleOptions(): WhiteboardPerson[] {
    return [...this.people.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  /** The signed-in person's week. A method, not a getter: see auth/userId.ts. */
  myWeekNow(): MyWeek {
    return myWeek(this.liveItems, currentUserIdOf(this.rootStore), this.currentWeek, this.today);
  }

  /** How many of the signed-in person's entries need them: this week's open ones and their overdue ones. */
  myOpenCount(): number {
    const week = this.myWeekNow();
    return week.thisWeek.filter(isOpenItem).length + week.overdue.length + week.blockers.length;
  }

  // ---- Accepted work, automatically (#2516) --------------------------------

  get laneWorkByClient(): Map<string, LaneWork> {
    return new Map(this.laneWork.map((work) => [work.clientId, work]));
  }

  laneWorkFor(clientId: string | null | undefined): LaneWork | null {
    return clientId ? (this.laneWorkByClient.get(clientId) ?? null) : null;
  }

  /** The Delivery lanes that are slipping, worst first (whiteboardTracking.ts). */
  get slipping(): SlippingLane[] {
    return slippingLanes({
      rows: this.allRows,
      items: this.liveItems,
      laneWork: (clientId) => this.laneWorkFor(clientId),
      today: this.today,
      horizonDays: this.slippingHorizonDays,
    });
  }

  // ---- The deal-status taxonomy (#3080 / #2662) -----------------------------

  /** Every deal status, retired ones included, in the taxonomy's order. */
  get statuses(): WhiteboardStatusRecord[] {
    return ((this.statusesService?.entities ?? []) as unknown as WhiteboardStatusRecord[])
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.label.localeCompare(b.label));
  }

  /** The statuses a lane may be set to. */
  get liveStatuses(): WhiteboardStatusRecord[] {
    return this.statuses.filter((s) => !s.deletedAt);
  }

  /** What a lane's deal status looks like (label, colours, category). */
  laneStatusMeta(key: string | null | undefined): LaneStatusMeta | null {
    return laneStatusMeta(this.statuses, key);
  }

  /** Sales Queue lanes per category, for the legend. */
  get dealCounts(): Record<LaneStatusCategory, number> {
    const counts: Record<LaneStatusCategory, number> = { active: 0, on_hold: 0, closed: 0 };
    for (const row of this.filteredRows) {
      if (row.board !== "sales") continue;
      const category = this.laneStatusMeta(row.status)?.category;
      if (category) counts[category] += 1;
    }
    return counts;
  }

  /** Summary counts over the active board's lanes for the CURRENT week. */
  get weekCounts(): Record<"in_process" | "done" | "needs_schedule" | "late", number> {
    const counts = { in_process: 0, done: 0, needs_schedule: 0, late: 0 };
    const week = this.currentWeek;
    for (const row of this.filteredRows) {
      const cell = this.cellFor(row.id, week);
      const status = (cell?.status ?? null) as keyof typeof counts | null;
      if (status && status in counts) counts[status] += 1;
    }
    return counts;
  }

  // ---- Filters / navigation ----------------------------------------------

  setBoard(board: WhiteboardBoard) {
    this.board = board;
  }

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  clearActionError() {
    this.actionError = null;
    this.removeError = null;
  }

  /** Narrow to one client — Client 360's "Open on Whiteboard" calls this
   *  before navigating, which is the whole cross-link contract. */
  focusClient(clientId: string | null) {
    this.clientFilter = clientId;
  }

  setResourceFilter(resource: string | null) {
    this.resourceFilter = resource?.trim() || null;
  }

  setWeekStatusFilter(status: WeekStatusFilter | null) {
    this.weekStatusFilter = status;
  }

  setDealStatusFilter(filter: DealStatusFilter | null) {
    this.dealStatusFilter = filter;
  }

  /** The Filters button's Clear: every filter it holds. The search box keeps
   *  its text; it has its own field to clear. */
  clearFilters() {
    this.clientFilter = null;
    this.resourceFilter = null;
    this.weekStatusFilter = null;
    this.dealStatusFilter = null;
  }

  shiftWeeks(delta: number) {
    this.weekAnchor = addWeeks(this.weekAnchor, delta);
    void this.loadCells();
    void this.loadItems();
  }

  goToCurrentWeek() {
    this.weekAnchor = currentWeekStart();
    void this.loadCells();
    void this.loadItems();
  }

  /** Show a view (Board, My week, Overdue & slipping). */
  setView(view: WhiteboardView) {
    this.view = view;
    // Overdue is measured against today: a page left open overnight catches up.
    this.today = todayLocal();
  }

  /**
   * A `?view=my-week` / `?view=slipping` link opens that view. No `view` in
   * the URL leaves the view alone, so coming back to the page (or its header
   * Refresh, which re-runs the route's onEnter) keeps the tab you were on.
   */
  showViewFromLink(raw: unknown) {
    if (typeof raw !== "string" || raw === "") return;
    this.setView(isView(raw) ? raw : "board");
  }

  setSlippingHorizon(days: number) {
    if (Number.isFinite(days) && days > 0) this.slippingHorizonDays = Math.round(days);
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    this.today = todayLocal();
    try {
      await Promise.all([
        // The library ServiceStore resolves null on a failed find; ask for the
        // rejection so a failed load never renders as "No lanes yet".
        //
        // The board asks for EVERY lane, so a lane the answer no longer holds
        // was archived elsewhere while this tab missed the event: drop it, or
        // the merge-only cache keeps it through every refresh (#2654).
        findAndEvict(
          this.rowsService,
          () => true,
          () => findOrThrow(this.rowsService, { query: { $limit: ROWS_LIMIT, $sort: { sortOrder: 1 } } }),
          { limit: ROWS_LIMIT },
        ),
        this.loadCells(),
        // Entries, deal statuses, accepted work and people each say their own
        // failure (itemsError, statusesError, laneWorkError) and never fail
        // the board: the lanes and week notes are still right without them.
        this.loadItems(),
        this.loadStatuses(),
        this.loadLaneWork(),
        this.loadPeople(),
        // Deliberately tolerant: the lane dialog's client picker + name
        // resolution. A failure only degrades lane labels to their ids.
        this.clientsService?.find({ query: { $limit: 500, $sort: { name: 1 } } }),
      ]);
    } catch (err: unknown) {
      if (ended()) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Failed to load whiteboard");
      });
      console.error("Failed to load whiteboard:", err);
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** Cells around the visible window (±4 weeks of slack so paging is instant). */
  async loadCells() {
    const from = addWeeks(this.weekAnchor, -(WEEKS_BEFORE + 4));
    const to = addWeeks(this.weekAnchor, WEEKS_AFTER + 4);
    const ended = sessionMark(this.rootStore);
    try {
      // Every cell of the window, so one the answer no longer holds was cleared
      // elsewhere: drop it (see load). Cells outside the window are not this
      // read's to judge and stay.
      const inWindow = (cell: Record<string, unknown>) => {
        const week = weekStartOf(String(cell.weekOf ?? ""));
        return week !== "" && week >= from && week <= to;
      };
      await findAndEvict(
        this.cellsService,
        inWindow,
        () => findOrThrow(this.cellsService, { query: { weekOf: { $gte: from, $lte: to }, $limit: CELLS_LIMIT } }),
        { limit: CELLS_LIMIT },
      );
      if (ended()) return;
      runInAction(() => {
        this.cellsError = null;
      });
    } catch (err: unknown) {
      if (ended()) return;
      runInAction(() => {
        this.cellsError = loadErrorMessage(err, "Failed to load whiteboard cells");
      });
      console.error("Failed to load whiteboard cells:", err);
    }
  }

  /**
   * Entries: every OPEN one, whatever week it was planned for — so an overdue
   * entry is on the Overdue list however far back it is (#3078) — plus every
   * entry of the board's weeks (and one either side), whatever its status, for
   * its columns. Every entry of that scope the answer no longer holds was resolved,
   * moved or archived elsewhere while this tab missed it: drop it (#2654).
   */
  async loadItems() {
    const service = this.itemsService;
    if (!service) return;
    // The visible weeks and one either side — not the cells' four: entries are
    // the bigger read (several per lane-week), and every page of weeks re-reads
    // them anyway, as it does the cells.
    const from = addWeeks(this.weekAnchor, -(WEEKS_BEFORE + ITEMS_SLACK_WEEKS));
    const to = addWeeks(this.weekAnchor, WEEKS_AFTER + ITEMS_SLACK_WEEKS);
    const ended = sessionMark(this.rootStore);
    const inScope = (item: Record<string, unknown>) => {
      if (item.status === "open") return true;
      const week = weekStartOf(String(item.weekOf ?? ""));
      return week !== "" && week >= from && week <= to;
    };
    try {
      await findAndEvict(
        service,
        inScope,
        () =>
          findOrThrow(service, {
            query: {
              $or: [{ status: "open" }, { weekOf: { $gte: from, $lte: to } }],
              $limit: ITEMS_LIMIT,
            },
          }),
        { limit: ITEMS_LIMIT },
      );
      if (ended()) return;
      runInAction(() => {
        this.itemsError = null;
      });
    } catch (err: unknown) {
      if (ended()) return;
      runInAction(() => {
        this.itemsError = loadErrorMessage(err, "The weekly entries could not be loaded");
      });
      console.error("Failed to load whiteboard entries:", err);
    }
  }

  /** The deal-status taxonomy, retired statuses included (they still label lanes). */
  async loadStatuses() {
    const service = this.statusesService;
    if (!service) return;
    const ended = sessionMark(this.rootStore);
    try {
      await findOrThrow(service, { query: { $limit: STATUSES_LIMIT, $sort: { sortOrder: 1 } } });
      if (ended()) return;
      runInAction(() => {
        this.statusesError = null;
      });
    } catch (err: unknown) {
      if (ended()) return;
      runInAction(() => {
        this.statusesError = loadErrorMessage(err, "The deal statuses could not be loaded");
      });
      console.error("Failed to load whiteboard deal statuses:", err);
    }
  }

  /**
   * Each client lane's accepted work, read from HQ's own action-item records
   * (#2516). Nobody types it: it moves when a developer accepts or completes
   * work, or Azure DevOps closes it (see subscribe()).
   */
  async loadLaneWork() {
    const service = this.laneWorkService;
    if (!service) return;
    const ended = sessionMark(this.rootStore);
    try {
      const res = await findOrThrow<LaneWorkResponse | null>(service, { query: {} });
      if (ended()) return;
      runInAction(() => {
        this.laneWork = Array.isArray(res?.data) ? res.data : [];
        this.laneWorkMeta = res
          ? { today: res.today, weekStart: res.weekStart, timeZone: res.timeZone, truncated: res.truncated === true }
          : null;
        this.laneWorkError = null;
      });
    } catch (err: unknown) {
      if (ended()) return;
      runInAction(() => {
        this.laneWorkError = loadErrorMessage(err, "Accepted work could not be loaded");
      });
      console.error("Failed to load whiteboard lane work:", err);
    }
  }

  /**
   * The people an entry can name. Tolerant: a failure only means owners show
   * as "User #id" and the owner picker is empty.
   */
  async loadPeople() {
    if (this.people.size > 0) return;
    await this.usersService
      ?.find({ query: { $limit: USERS_LIMIT } })
      ?.catch?.(() => undefined);
  }

  /**
   * Re-read the lanes' accepted work when a developer accepts, completes or
   * an operator declines an action item anywhere in HQ, or when work moves to
   * another module or client (set-module, a retarget; the API publishes all
   * of these to staff, a private meeting's only to its people). Debounced: a
   * backlog sweep completes many at once. Started by the route's onEnter,
   * ended by its onExit.
   */
  subscribe() {
    if (this.laneWorkUnsubscribe) return;
    this.laneWorkUnsubscribe = subscribeMeetingIntelEvents(this.rootStore, (event) => {
      if (
        event.kind !== "assignment-accepted" &&
        event.kind !== "assignment-completed" &&
        event.kind !== "assignment-rejected" &&
        // Moved to another module, or — a retarget — to another client's lane
        // (2026-11-02, Codex r1): lane work is per client and names modules.
        event.kind !== "assignment-module-changed"
      ) {
        return;
      }
      if (this.laneWorkTimer) clearTimeout(this.laneWorkTimer);
      this.laneWorkTimer = setTimeout(() => {
        this.laneWorkTimer = null;
        void this.loadLaneWork();
      }, LANE_WORK_DEBOUNCE_MS);
    });
  }

  dispose() {
    if (this.laneWorkTimer) {
      clearTimeout(this.laneWorkTimer);
      this.laneWorkTimer = null;
    }
    if (this.laneWorkUnsubscribe) {
      this.laneWorkUnsubscribe();
      this.laneWorkUnsubscribe = null;
    }
  }

  // ---- Cell dialog --------------------------------------------------------

  openCell(row: WhiteboardRowRecord, weekOf: string) {
    const existing = this.cellFor(row.id, weekOf);
    this.actionError = null;
    this.removeError = null;
    this.cellDraft = {
      rowId: row.id,
      rowTitle: [row.title, row.resource].filter(Boolean).join(" · "),
      weekOf,
      cellId: existing?.id ?? null,
      status: existing?.status ?? "in_process",
      note: existing?.note ?? "",
    };
  }

  setCellStatus(status: WhiteboardStatus | string) {
    if (this.cellDraft) this.cellDraft.status = status;
  }

  setCellNote(note: string) {
    if (this.cellDraft) this.cellDraft.note = note;
  }

  /** Dialog submit: create the week's cell or patch the existing one. */
  async submitCellDialog(): Promise<boolean> {
    const draft = this.cellDraft;
    if (!draft) return false;
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.actionError = null;
    this.removeError = null;
    try {
      if (draft.cellId) {
        await this.cellsService?.patch(
          draft.cellId,
          { status: draft.status, note: draft.note || null } as never,
          THROW_ON_ERROR,
        );
      } else {
        await this.cellsService?.create(
          {
            rowId: draft.rowId,
            weekOf: draft.weekOf,
            status: draft.status,
            note: draft.note || null,
          } as never,
          THROW_ON_ERROR,
        );
      }
      if (ended()) return false;
      runInAction(() => {
        this.cellDraft = null;
      });
      return true;
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.actionError = err instanceof Error ? err.message : "Failed to save cell";
      });
      console.error("whiteboard cell save failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  /** Clear (soft-delete) the cell being edited. */
  async clearEditingCell(): Promise<boolean> {
    const draft = this.cellDraft;
    if (!draft?.cellId) {
      this.cellDraft = null;
      return true;
    }
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.removeError = null;
    try {
      await this.cellsService?.remove(draft.cellId, THROW_ON_ERROR);
      if (ended()) return false;
      runInAction(() => {
        this.cellDraft = null;
      });
      return true;
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.removeError = err instanceof Error ? err.message : "Failed to clear cell";
      });
      console.error("whiteboard cell clear failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  // ---- Lane dialog --------------------------------------------------------

  /**
   * Seed an empty lane. `clientId` pins it to one client (Client 360's Add
   * lane) WITHOUT narrowing the board to that client — `focusClient` is the
   * board's own filter, and adding a lane from Client 360 must not leave the
   * Whiteboard silently filtered on the next visit. Omitted, the board's
   * current client focus (if any) is used.
   */
  openNewRow(options: { clientId?: string | null } = {}) {
    this.actionError = null;
    this.removeError = null;
    this.rowDraft = {
      rowId: null,
      board: this.board,
      clientId:
        options.clientId !== undefined ? options.clientId : this.clientFilter,
      title: "",
      resource: "",
      workstream: "",
      notes: "",
      status: this.board === "sales" ? this.defaultSalesStatus : null,
      targetDate: "",
      targetLabel: "",
    };
  }

  /** A new Sales Queue lane starts at the taxonomy's first active status. */
  get defaultSalesStatus(): string {
    return (
      this.liveStatuses.find((s) => s.category === "active")?.statusKey ??
      this.liveStatuses[0]?.statusKey ??
      "in_process"
    );
  }

  /** The Add lane control's action: seed the draft and open the lane dialog. */
  startAddLane(options: { clientId?: string | null } = {}) {
    this.openNewRow(options);
    this.warmClients();
    this.rootStore.routerStore?.openDialog?.({
      dialogName: WHITEBOARD_ROW_DIALOG,
    });
  }

  /**
   * Move to Delivery (#3558): the one action that takes a won prospect off the
   * Sales Queue and onto the Delivery board. It opens the lane dialog on this
   * very lane, already set to Delivery, so the lane keeps its notes and its
   * weekly history, and its sales status (a sales-board field) is dropped.
   * Saving it (submitRowDialog) first makes the lane's client Active if it is
   * still a Prospect, which is the status change the board never made. Only a
   * Sales Queue lane can be moved.
   */
  startMoveToDelivery(row: WhiteboardRowRecord) {
    if (row.board !== "sales") return;
    this.openEditRow(row);
    if (!this.rowDraft) return;
    this.rowDraft.board = "delivery";
    this.rowDraft.status = null;
    this.rowDraft.moveToDelivery = true;
    this.warmClients();
    this.rootStore.routerStore?.openDialog?.({
      dialogName: WHITEBOARD_ROW_DIALOG,
    });
  }

  /**
   * The lane dialog's client picker reads the clients cache, which Client 360
   * does not necessarily fill, so it is warmed here (best-effort: a failure
   * only leaves the picker empty).
   */
  private warmClients() {
    if (this.clients.length > 0) return;
    void this.clientsService
      ?.find({ query: { $limit: 500, $sort: { name: 1 } } })
      ?.catch?.(() => undefined);
  }

  openEditRow(row: WhiteboardRowRecord) {
    this.actionError = null;
    this.removeError = null;
    this.rowDraft = {
      rowId: row.id,
      board: row.board,
      clientId: row.clientId ?? null,
      title: row.title,
      resource: row.resource ?? "",
      workstream: row.workstream ?? "",
      notes: row.notes ?? "",
      status: row.status ?? null,
      targetDate: row.targetDate ?? "",
      targetLabel: row.targetLabel ?? "",
    };
  }

  patchRowDraft(patch: Partial<RowDraft>) {
    if (this.rowDraft) Object.assign(this.rowDraft, patch);
  }

  get rowDraftValid(): boolean {
    const draft = this.rowDraft;
    if (!draft?.title.trim()) return false;
    // A lane moving to Delivery is a client's delivery work: it names one.
    return !draft.moveToDelivery || !!draft.clientId;
  }

  async submitRowDialog(): Promise<boolean> {
    const draft = this.rowDraft;
    if (!draft || !draft.title.trim()) return false;
    if (draft.moveToDelivery && !draft.clientId) {
      this.actionError = "Pick the client this lane is delivery work for.";
      return false;
    }
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.actionError = null;
    this.removeError = null;
    const payload = {
      board: draft.board,
      clientId: draft.clientId,
      title: draft.title.trim(),
      resource: draft.resource.trim() || null,
      workstream: draft.workstream.trim() || null,
      notes: draft.notes.trim() || null,
      status: draft.status,
      targetDate: draft.targetDate || null,
      targetLabel: draft.targetLabel.trim() || null,
    };
    const moveClientId = draft.moveToDelivery ? draft.clientId : null;
    const clientLabel = this.clientName(moveClientId) ?? payload.title;
    let activation: Activation = "not-prospect";
    try {
      // A move makes the client Active FIRST: if that is refused, nothing has
      // moved and the dialog says why.
      if (moveClientId) activation = await this.activateProspect(moveClientId);
      if (ended()) return false;
      try {
        // Per call `throwOnError`: the ServiceStore otherwise SWALLOWS a refusal
        // (resolves undefined), and the lane dialog closed as though it had saved.
        if (draft.rowId) {
          await this.rowsService?.patch(draft.rowId, payload as never, THROW_ON_ERROR);
        } else {
          await this.rowsService?.create(payload as never, THROW_ON_ERROR);
        }
      } catch (laneErr: unknown) {
        // The two writes are not one transaction, so a refused lane write
        // puts the client back as it was: a failed move changes nothing.
        if (activation === "activated" && moveClientId && !ended()) {
          if (!(await this.undoActivation(moveClientId))) {
            const reason = laneErr instanceof Error ? laneErr.message : "The lane could not be moved.";
            throw new Error(
              `${reason} ${clientLabel} is Active now, and the lane is still on the Sales Queue.`,
            );
          }
        }
        throw laneErr;
      }
      if (ended()) return false;
      runInAction(() => {
        this.rowDraft = null;
      });
      if (draft.moveToDelivery) {
        // The lane leaves the Sales Queue under the person's eyes: say where.
        this.rootStore.notificationStore?.addSuccessNotification?.(
          activation === "activated"
            ? `${payload.title} moved to Delivery, and ${clientLabel} is now an active client.`
            : activation === "changed"
              ? `${payload.title} moved to Delivery. ${clientLabel} is no longer a Prospect, so its status was left as it is.`
              : `${payload.title} moved to Delivery.`,
        );
      }
      return true;
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.actionError = err instanceof Error ? err.message : "Failed to save lane";
      });
      console.error("whiteboard lane save failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  /**
   * Prospect → Active for a client moving into delivery. A client the clients
   * cache (or, when the cache does not hold it, the server) shows as anything
   * but a Prospect is left alone. The write itself is conditional ON THE
   * SERVER (`query: { status: "prospect" }`): the cache can be stale, and a
   * client someone made Inactive since the board loaded must stay Inactive. A
   * NotFound from that write means it is no longer a Prospect ("changed").
   * Any other refusal throws.
   */
  private async activateProspect(clientId: string): Promise<Activation> {
    const service = this.clientsService;
    if (!service) return "not-prospect";
    let isProspect = this.clientIsProspect(clientId);
    const cached = (service.entities ?? []) as unknown as Array<{ id: string }>;
    if (!isProspect && !cached.some((c) => c.id === clientId)) {
      const fetched = (await service.get(clientId, THROW_ON_ERROR)) as unknown as
        | { status?: unknown }
        | undefined;
      isProspect = isProspectStatus(fetched?.status);
    }
    if (!isProspect) return "not-prospect";
    try {
      await service.patch(clientId, { status: "active" } as never, {
        query: { status: "prospect" },
        throwOnError: true,
      } as never);
      return "activated";
    } catch (err: unknown) {
      if (isNotFoundError(err)) return "changed";
      throw err;
    }
  }

  /**
   * Back to Prospect after a move whose lane write failed, conditional on the
   * client still being the Active this move made it. False only when that
   * write fails: the caller then says the client was left Active.
   */
  private async undoActivation(clientId: string): Promise<boolean> {
    try {
      await this.clientsService?.patch(clientId, { status: "prospect" } as never, {
        query: { status: "active" },
        throwOnError: true,
      } as never);
      return true;
    } catch (err: unknown) {
      // Someone changed it again meanwhile: theirs stands, nothing to undo.
      if (isNotFoundError(err)) return true;
      console.error("whiteboard move: putting the client back to Prospect failed:", err);
      return false;
    }
  }

  /** Archive (soft-delete) the lane being edited. */
  async archiveEditingRow(): Promise<boolean> {
    const draft = this.rowDraft;
    if (!draft?.rowId) return false;
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.removeError = null;
    try {
      await this.rowsService?.remove(draft.rowId, THROW_ON_ERROR);
      if (ended()) return false;
      runInAction(() => {
        this.rowDraft = null;
      });
      return true;
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.removeError = err instanceof Error ? err.message : "Failed to archive lane";
      });
      console.error("whiteboard lane archive failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  // ---- Entries: the dialog, the lists and the quick add (#3383, #3078) ----

  /** May the signed-in person change this entry? Its owner, or an operator or admin. */
  canEditItem(item: Pick<WhiteboardItemRecord, "ownerUserId">): boolean {
    const me = currentUserIdOf(this.rootStore);
    return (me !== null && Number(item.ownerUserId) === me) || isOperator(this.rootStore);
  }

  /** May they log an entry for someone else, or hand one over? Operators and admins. */
  canAssignOthers(): boolean {
    return isOperator(this.rootStore);
  }

  /** May they add or edit LANES and week notes? Operators and admins (#3382). */
  canEditLanes(): boolean {
    return isOperator(this.rootStore);
  }

  /**
   * May they ARCHIVE a lane? Admins only (operator ruling 2026-11-02; it was
   * operator+). The API refuses anyone else (whiteboard-rows
   * LANE_ARCHIVE_REFUSAL); this only decides whether the button is live.
   */
  canArchiveLanes(): boolean {
    return isAdmin(this.rootStore);
  }

  /**
   * May they CLEAR a week (the week note's Clear cell)? Admins only
   * (2026-11-02). Operators still write and change the note; the API refuses
   * the clear to anyone else (whiteboard-cells WEEK_CLEAR_REFUSAL).
   */
  canClearWeeks(): boolean {
    return isAdmin(this.rootStore);
  }

  /** May they edit the deal statuses? Admins. */
  canEditStatuses(): boolean {
    return isAdmin(this.rootStore);
  }

  /** Seed a new entry, on a lane and week when the caller knows them (a board cell). */
  openNewItem(options: { rowId?: string | null; weekOf?: string; kind?: WhiteboardItemKind } = {}) {
    const week = (options.weekOf && weekStartOf(options.weekOf)) || this.currentWeek;
    const kind = options.kind ?? "goal";
    this.actionError = null;
    this.removeError = null;
    this.itemDraft = {
      itemId: null,
      rowId: options.rowId ?? null,
      kind,
      title: "",
      note: "",
      ownerUserId: null,
      weekOf: week,
      dueDate: kind === "blocker" ? "" : defaultDueFor(week),
      status: "open",
    };
  }

  /** The Log entry action: seed the draft and open the entry dialog. */
  startLogEntry(options: { rowId?: string | null; weekOf?: string; kind?: WhiteboardItemKind } = {}) {
    this.openNewItem(options);
    void this.loadPeople();
    this.rootStore.routerStore?.openDialog?.({ dialogName: WHITEBOARD_ENTRY_DIALOG });
  }

  openEditItem(item: WhiteboardItemRecord) {
    this.actionError = null;
    this.removeError = null;
    this.itemDraft = {
      itemId: item.id,
      rowId: item.rowId,
      kind: (item.kind as WhiteboardItemKind) ?? "goal",
      title: item.title,
      note: item.note ?? "",
      ownerUserId: Number(item.ownerUserId),
      weekOf: item.weekOf,
      dueDate: item.dueDate ?? "",
      status: (item.status as WhiteboardItemStatus) ?? "open",
    };
  }

  /** Open an existing entry in the entry dialog. */
  startEditItem(item: WhiteboardItemRecord) {
    this.openEditItem(item);
    void this.loadPeople();
    this.rootStore.routerStore?.openDialog?.({ dialogName: WHITEBOARD_ENTRY_DIALOG });
  }

  patchItemDraft(patch: Partial<ItemDraft>) {
    if (!this.itemDraft) return;
    if (patch.weekOf !== undefined) patch = { ...patch, weekOf: weekStartOf(patch.weekOf) || this.itemDraft.weekOf };
    Object.assign(this.itemDraft, patch);
  }

  get itemDraftValid(): boolean {
    const draft = this.itemDraft;
    return !!draft && !!draft.rowId && draft.title.trim().length > 0 && !!draft.weekOf;
  }

  /**
   * May the signed-in person save the entry dialog? A new entry always (the
   * server makes it theirs); an existing one when they own it or are an
   * operator or admin. A method, not a getter: it asks who is signed in.
   */
  itemDialogEditable(): boolean {
    const draft = this.itemDraft;
    if (!draft) return false;
    if (!draft.itemId) return true;
    const stored = this.storedItem(draft.itemId);
    return this.canEditItem({ ownerUserId: Number(stored?.ownerUserId ?? draft.ownerUserId ?? NaN) });
  }

  /** The entry the dialog is editing, as the cache holds it now. */
  private storedItem(id: string | null): WhiteboardItemRecord | null {
    return id ? (this.allItems.find((item) => item.id === id) ?? null) : null;
  }

  /** Dialog submit: log the entry, or save the edit. A refusal throws into the dialog footer. */
  async submitItemDialog(): Promise<boolean> {
    const draft = this.itemDraft;
    if (!draft || !this.itemDraftValid) return false;
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.actionError = null;
    this.removeError = null;
    const body: Record<string, unknown> = {
      rowId: draft.rowId,
      kind: draft.kind,
      title: draft.title.trim(),
      note: draft.note.trim() || null,
      weekOf: draft.weekOf,
      dueDate: draft.dueDate || null,
    };
    try {
      if (draft.itemId) {
        const stored = this.storedItem(draft.itemId);
        body.status = draft.status;
        // Only a change of owner is sent: the server refuses a handover from
        // anyone but an operator or admin, and an unchanged owner is no handover.
        if (draft.ownerUserId !== null && Number(stored?.ownerUserId) !== draft.ownerUserId) {
          body.ownerUserId = draft.ownerUserId;
        }
        await this.itemsService?.patch(draft.itemId, body as never, THROW_ON_ERROR);
      } else {
        if (draft.ownerUserId !== null) body.ownerUserId = draft.ownerUserId;
        await this.itemsService?.create(body as never, THROW_ON_ERROR);
      }
      if (ended()) return false;
      runInAction(() => {
        this.itemDraft = null;
      });
      return true;
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.actionError = err instanceof Error ? err.message : "The entry could not be saved.";
      });
      console.error("whiteboard entry save failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  /** Archive the entry the dialog is editing (its owner, or an operator or admin). */
  async removeEditingItem(): Promise<boolean> {
    const draft = this.itemDraft;
    if (!draft?.itemId) return false;
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.removeError = null;
    try {
      await this.itemsService?.remove(draft.itemId, THROW_ON_ERROR);
      if (ended()) return false;
      runInAction(() => {
        this.itemDraft = null;
      });
      return true;
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.removeError = err instanceof Error ? err.message : "The entry could not be removed.";
      });
      console.error("whiteboard entry remove failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  /** One patch from a list (Done, Drop, Reopen, a new date). A refusal shows above the list. */
  private async patchFromList(item: WhiteboardItemRecord, body: Record<string, unknown>): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.listError = null;
    try {
      await this.itemsService?.patch(item.id, body as never, THROW_ON_ERROR);
      return !ended();
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.listError = `${item.title}: ${err instanceof Error ? err.message : "the change could not be saved."}`;
      });
      console.error("whiteboard entry update failed:", err);
      return false;
    }
  }

  /** Done, dropped, or open again. */
  setItemStatus(item: WhiteboardItemRecord, status: WhiteboardItemStatus): Promise<boolean> {
    return this.patchFromList(item, { status });
  }

  /**
   * A new date takes an entry off the Overdue list: it moves to the week the
   * date falls in, and stays open.
   */
  rescheduleItem(item: WhiteboardItemRecord, dueDate: string): Promise<boolean> {
    const week = weekStartOf(dueDate);
    if (!week) return Promise.resolve(false);
    return this.patchFromList(item, { dueDate, weekOf: week });
  }

  clearListError() {
    this.listError = null;
  }

  patchQuickDraft(patch: Partial<QuickDraft>) {
    const next = { ...this.quickDraft, ...patch };
    if (patch.weekOf !== undefined) {
      next.weekOf = weekStartOf(patch.weekOf) || this.quickDraft.weekOf;
      // Moving the week moves the default due day with it (a blocker has none).
      if (patch.dueDate === undefined && next.kind !== "blocker") next.dueDate = defaultDueFor(next.weekOf);
    }
    if (patch.kind === "blocker" && patch.dueDate === undefined) next.dueDate = "";
    if (patch.kind && patch.kind !== "blocker" && this.quickDraft.kind === "blocker" && patch.dueDate === undefined) {
      next.dueDate = defaultDueFor(next.weekOf);
    }
    this.quickDraft = next;
    this.quickError = null;
  }

  get quickDraftValid(): boolean {
    return !!this.quickDraft.rowId && this.quickDraft.title.trim().length > 0;
  }

  /**
   * My week's quick add: one entry, the signed-in person as owner, and the
   * lane and week kept for the next line — logging a week is several entries
   * in a row on the same lane.
   */
  async submitQuickAdd(): Promise<boolean> {
    const draft = this.quickDraft;
    if (!this.quickDraftValid) return false;
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.quickError = null;
    try {
      await this.itemsService?.create(
        {
          rowId: draft.rowId,
          kind: draft.kind,
          title: draft.title.trim(),
          weekOf: draft.weekOf,
          dueDate: draft.dueDate || null,
        } as never,
        THROW_ON_ERROR,
      );
      if (ended()) return false;
      runInAction(() => {
        this.quickDraft = { ...this.quickDraft, title: "" };
      });
      return true;
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.quickError = err instanceof Error ? err.message : "The entry could not be logged.";
      });
      console.error("whiteboard quick add failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  // ---- Deal statuses: the admin editor (#3080 / #2662) ---------------------

  /** Open the deal-status editor with nothing pending. */
  startEditStatuses() {
    this.statusEdits = {};
    this.newStatus = emptyStatusEdit();
    this.statusError = null;
    this.statusWriteError = null;
    void this.loadStatuses();
    this.rootStore.routerStore?.openDialog?.({ dialogName: WHITEBOARD_STATUSES_DIALOG });
  }

  /** What the editor shows for a status: the pending edit, else what is stored. */
  statusEditFor(status: WhiteboardStatusRecord): StatusEdit {
    return this.statusEdits[status.id] ?? { label: status.label, color: status.color, category: status.category };
  }

  editStatus(status: WhiteboardStatusRecord, patch: Partial<StatusEdit>) {
    this.statusEdits = { ...this.statusEdits, [status.id]: { ...this.statusEditFor(status), ...patch } };
    this.statusError = null;
  }

  patchNewStatus(patch: Partial<StatusEdit>) {
    this.newStatus = { ...this.newStatus, ...patch };
    this.statusError = null;
  }

  /** The statuses whose pending edit differs from what is stored. */
  get dirtyStatuses(): WhiteboardStatusRecord[] {
    return this.statuses.filter((status) => {
      const edit = this.statusEdits[status.id];
      return (
        !!edit &&
        (edit.label.trim() !== status.label || edit.color !== status.color || edit.category !== status.category)
      );
    });
  }

  get statusEditsValid(): boolean {
    return this.dirtyStatuses.every((status) => this.statusEdits[status.id].label.trim().length > 0);
  }

  /** The dialog's Save: every pending edit. A refusal throws into the footer, the rest stay pending. */
  async saveStatusEdits(): Promise<boolean> {
    const dirty = this.dirtyStatuses;
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.statusError = null;
    try {
      for (const status of dirty) {
        const edit = this.statusEdits[status.id];
        await this.statusesService?.patch(
          status.id,
          { label: edit.label.trim(), color: edit.color, category: edit.category } as never,
          THROW_ON_ERROR,
        );
        if (ended()) return false;
        runInAction(() => {
          const rest = { ...this.statusEdits };
          delete rest[status.id];
          this.statusEdits = rest;
        });
      }
      return true;
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.statusError = err instanceof Error ? err.message : "The deal statuses could not be saved.";
      });
      console.error("whiteboard status save failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  /** One immediate write from inside the editor (add, retire, restore, reorder). */
  private async statusWrite(run: () => Promise<unknown>, failure: string): Promise<boolean> {
    const ended = sessionMark(this.rootStore);
    this.processing = true;
    this.statusWriteError = null;
    try {
      await run();
      return !ended();
    } catch (err: unknown) {
      if (ended()) return false;
      runInAction(() => {
        this.statusWriteError = err instanceof Error ? err.message : failure;
      });
      console.error("whiteboard status write failed:", err);
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.processing = false;
        });
      }
    }
  }

  async addStatus(): Promise<boolean> {
    const draft = this.newStatus;
    if (!draft.label.trim()) return false;
    const ended = sessionMark(this.rootStore);
    const ok = await this.statusWrite(
      () =>
        this.statusesService?.create(
          { label: draft.label.trim(), color: draft.color, category: draft.category } as never,
          THROW_ON_ERROR,
        ) ?? Promise.resolve(),
      "The deal status could not be added.",
    );
    if (ended()) return false;
    if (ok) {
      runInAction(() => {
        this.newStatus = emptyStatusEdit();
      });
    }
    return ok;
  }

  /**
   * Retire: out of the pickers, still the label of every lane that holds it. A
   * PATCH of `deletedAt`, never a remove: a remove evicts the row from every
   * open board's cache (the API has none, for that reason).
   */
  retireStatus(status: WhiteboardStatusRecord): Promise<boolean> {
    return this.statusWrite(
      () =>
        this.statusesService?.patch(status.id, { deletedAt: new Date().toISOString() } as never, THROW_ON_ERROR) ??
        Promise.resolve(),
      "The deal status could not be retired.",
    );
  }

  restoreStatus(status: WhiteboardStatusRecord): Promise<boolean> {
    return this.statusWrite(
      () => this.statusesService?.patch(status.id, { deletedAt: null } as never, THROW_ON_ERROR) ?? Promise.resolve(),
      "The deal status could not be restored.",
    );
  }

  /** Swap a status with its neighbour in the order (`delta` -1 = up). */
  moveStatus(status: WhiteboardStatusRecord, delta: -1 | 1): Promise<boolean> {
    const list = this.statuses;
    const at = list.findIndex((s) => s.id === status.id);
    const other = list[at + delta];
    if (at < 0 || !other) return Promise.resolve(false);
    // Distinct orders even if two were equal, so a swap always moves something.
    const mine = other.sortOrder === status.sortOrder ? status.sortOrder + delta : other.sortOrder;
    const theirs = status.sortOrder;
    return this.statusWrite(async () => {
      await this.statusesService?.patch(status.id, { sortOrder: mine } as never, THROW_ON_ERROR);
      await this.statusesService?.patch(other.id, { sortOrder: theirs } as never, THROW_ON_ERROR);
    }, "The order could not be saved.");
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out is
   * left for the next one: the client, owner, week and deal filters, the
   * search, the week window and view, the half-typed cell note, lane, entry,
   * quick add and status edits in the open dialogs, the lanes' accepted work,
   * and the live subscription that re-reads it. Called by RootStore.purgeUserData on sign-out;
   * the lanes and cells themselves are the ServiceStores' caches, which the
   * purge clears.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.cellsError = null;
    this.board = "delivery";
    this.searchQuery = "";
    this.clientFilter = null;
    this.resourceFilter = null;
    this.weekStatusFilter = null;
    this.weekAnchor = currentWeekStart();
    this.processing = false;
    this.actionError = null;
    this.removeError = null;
    this.cellDraft = null;
    this.rowDraft = null;
    this.view = "board";
    this.slippingHorizonDays = 14;
    this.dealStatusFilter = null;
    this.today = todayLocal();
    this.itemsError = null;
    this.statusesError = null;
    this.laneWork = [];
    this.laneWorkMeta = null;
    this.laneWorkError = null;
    this.itemDraft = null;
    this.quickDraft = emptyQuickDraft();
    this.quickError = null;
    this.listError = null;
    this.statusEdits = {};
    this.newStatus = emptyStatusEdit();
    this.statusError = null;
    this.statusWriteError = null;
    this.dispose();
  }
}

export default WhiteboardStore;
